import {
  DataStateChangeEvent,
  FilterService,
  GridDataResult,
} from '@progress/kendo-angular-grid';
import {
  ColumnGroup,
  ColumnOptions,
  GridRowType,
  GridState,
  GridStateCompositeFilterDescriptor,
  LoaderService,
  MessageService,
  ValidationResult,
  ValidationResultService,
} from '@shared';
import {
  CategoryService,
  GlobalEventsService,
  IChangeset,
} from '@shared-data';
import { RestaurantItemService } from '../../services';
import {
  CompositeFilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import { Injector, OnDestroy } from '@angular/core';
import { ExportActiveFilter, ExportOption, ImportOptions } from '../../constants';

export abstract class ChangesetComponent{
  columns: ColumnOptions[] = [];
  columnGroups: ColumnGroup[] = [];

  entity!: IChangeset & GridRowType;

  changeset: GridDataResult = { data: [], total: 0 };

  cache: Map<string, IChangeset & GridRowType> = new Map<
    string,
    IChangeset & GridRowType
  >();

  restaurantId?: string;
  restaurantIds?: string[];

  category: string | null = null;

  type!: string;

  state!: GridState;

  _fontSize: number = 0.875;

  set fontSize(value: number) {
    this._fontSize = value;
    this.setDefaultState(false);
  }

  get fontSize(): number {
    return this._fontSize;
  }

  importUrls!: Map<ImportOptions, string>;

  initialSort!: SortDescriptor[];
  initialFilter!: CompositeFilterDescriptor;

  filterCache: Map<string, string[]> = new Map<string, string[]>();

  _productTypeId!: string;

  set productTypeId(v: string) {
    this._productTypeId = v;
    if (v) {
      this.importUrls = this.service.getImportUrls(v, this.type);
    }
  }
  get productTypeId(): string {
    return this._productTypeId;
  }

  protected globalEventsService: GlobalEventsService;

  constructor(
    protected validationErrorService: ValidationResultService,
    protected messageService: MessageService,
    protected service: RestaurantItemService,
    protected categoryService: CategoryService,
    protected loaderService: LoaderService,
    protected injector: Injector,
    protected entityType?: new (...args: any[]) => IChangeset & GridRowType,
  ) {
    this.globalEventsService = this.injector.get(GlobalEventsService);
    this.entity = new this.entityType!();
    this.setColumns();
  }

  setColumns() {
    this.columns = this.entity.getColumns();
    this.columnGroups = this.entity.getColumnGroups(this.columns);
    this.setInitialSort();
    this.setInitialFilter();
  }

  setInitialSort() {
    this.initialSort = [
      { field: 'categoryOrder', dir: 'asc' },
      { field: 'sortOrder', dir: 'asc' },
    ];
  }

  setInitialFilter() {
    this.initialFilter = {
      logic: 'and',
      filters: [
      ]
    }
  }

  // Field the value picked on the toolbar sub-selector filters the grid by.
  // Every product type filters by category except Bundles, which filters by
  // the bundle container id
  protected getSubSelectorFilterField(): string {
    return 'categoryId';
  }

  // Rows the grid opens with. It follows the font size and the screen by
  // default, and a product type can ask for its own size
  protected getPageSize(): number {
    return GridState.getPageSizeByFontSize(this.fontSize);
  }

  private get gridStateStorageKey(): string {
    return [
      'changeset-grid-state',
      this.type,
      this.productTypeId,
      this.restaurantId ?? '',
      (this.restaurantIds ?? []).join(','),
    ].join(':');
  }

  onDataStateChange(event: DataStateChangeEvent): void {
    this.saveGridState(event);
    this.fetchChangesets(event);
  }

  private saveGridState(event: DataStateChangeEvent | GridState): void {
    try {
      sessionStorage.setItem(
        this.gridStateStorageKey,
        JSON.stringify({
          filter: event.filter,
          sort: event.sort,
          skip: event.skip,
          take: event.take,
          filterCache: Array.from(this.filterCache.entries()),
        }),
      );
    } catch {
      
    }
  }

  private restoreGridState(): void {
    let saved: any;
    try {
      saved = JSON.parse(sessionStorage.getItem(this.gridStateStorageKey) ?? 'null');
    } catch {
      return;
    }
    if (!saved) {
      return;
    }

    if (saved.filter) {
      this.state.filter = new GridStateCompositeFilterDescriptor(
        this.reviveFilterDates(saved.filter),
      );
    }
    if (saved.sort?.length) {
      this.state.sort = saved.sort;
    }
    if (saved.skip !== undefined) {
      this.state.skip = saved.skip;
    }
    if (saved.take !== undefined) {
      this.state.take = saved.take;
    }
    this.filterCache = new Map<string, string[]>(saved.filterCache ?? []);
  }

  private reviveFilterDates(filter: CompositeFilterDescriptor): CompositeFilterDescriptor {
    return {
      ...filter,
      filters: filter.filters.map((f: any) => {
        if ('filters' in f) {
          return this.reviveFilterDates(f);
        }
        const column = this.columns.find((c) => c.field === f.field);
        if (column?.filterType === 'date' && typeof f.value === 'string') {
          return { ...f, value: new Date(f.value) };
        }
        return f;
      }),
    };
  }

  setDefaultState(restoreSavedState: boolean = true): void {
    if (
      !this.restaurantId &&
      (!this.restaurantIds || this.restaurantIds.length === 0)
    ) {
      return;
    }

    this.state = new GridState(
      this.getPageSize(),
      this.initialSort,
      this.restaurantId,
      this.productTypeId,
      this.restaurantIds,
    );

    this.state.filter = this.state.filter?.and(this.initialFilter);

    let newFilter = new GridStateCompositeFilterDescriptor();

    this.columns.forEach((column) => {
      const values = this.filterCache.get(column.field);
      values?.forEach((val) => {
        newFilter = newFilter?.or({
          field: column.field,
          operator: 'eq',
          value: val,
        });
      });
    });

    this.state.filter = this.state.filter?.and(newFilter);

    if (restoreSavedState) {
      this.restoreGridState();
    }

    this.fetchChangesets(this.state);
  }

  fetchChangesets(event: GridState | DataStateChangeEvent) {
    const state = event as GridState;

    if (state.filter) {
      this.state.filter = state.filter;
    }
    if (state.sort?.length) {
      this.state.sort = state.sort;
    } else {
      this.state.sort = this.initialSort;
    }

    if (state.skip !== undefined) this.state.skip = state.skip;

    if (state.take !== undefined) this.state.take = state.take;

    const subSelectorFilterField = this.getSubSelectorFilterField();

    if (this.state.filter)
      this.state.filter = new GridStateCompositeFilterDescriptor(
        GridState.removeFilterByFields(
          this.state.filter,
          subSelectorFilterField,
        ),
      );

    if (this.category != null) {
      this.state.filter = this.state.filter?.and({
        field: subSelectorFilterField,
        operator: 'eq',
        value: this.category,
      });
    }

    this.state.restaurantId = this.restaurantId;
    this.state.productType = this.productTypeId;
    this.state.restaurantIds = this.restaurantIds;

    this.loaderService.show();

    this.service.getChangesets(this.type, this.state).subscribe({
      next: (gridDataResult) => {
        this.populateGrid(gridDataResult);
        this.restoreCachedChanges();
        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error('Failed to load changesets');

        var validationError = err?.error;

        if (validationError) {
          this.validationErrorService.show(validationError);
        }

        this.loaderService.hide();
      },
    });
  }

  // hook for populating grid data
  abstract populateGrid(gridDataResult: GridDataResult): void;

  restoreCachedChanges(): void {
    this.changeset.data.forEach((row) => {
      const cachedRow = this.cache.get(row.id);

      if (cachedRow) {
        const editableColumns = this.columns.filter(
          (column) => column.editable,
        );
        editableColumns.forEach((column) => {
          row[column.field as keyof IChangeset] =
            cachedRow[column.field as keyof IChangeset];
        });

        row.dirty = cachedRow.dirty;
      }
    });
  }

  cacheChanges() {
    this.changeset.data.forEach((row) => {
      const editableColumns = this.columns.filter((column) => column.editable);
      const isDirty = editableColumns.some((column) =>
        row.dirty.get(column.field),
      );
      if (isDirty) {
        this.cache.set(row.id, row);
      } else {
        this.cache.delete(row.id);
      }

      row.warnings.clear();
      row.errors.clear();
    });
  }

  saveChanges(applyToRestaurants?: string[]) {
    this.loaderService.show();
    const changes = Array.from(this.cache.values());
    this.service
      .saveChangesets(
        this.type,
        changes,
        this.productTypeId,
        applyToRestaurants,
      )
      .subscribe({
        next: (result) => {
          this.messageService.success('Changes saved successfully');
          if (result?.errors?.length) {
            this.validationErrorService.show(result);
          }
          this.cache.clear();

          this.clearDirtyRows();

          this.fetchChangesets(this.state);

          this.globalEventsService.updatePendingChanges();

          this.loaderService.hide();
        },
        error: (err) => {
          this.messageService.error(
            'Changes could not be saved due to validation errors',
          );
          const validationError = err?.error;
          if (validationError) {
            this.validationErrorService.show(validationError);
          }

          this.loaderService.hide();
        },
      });
  }

  clearDirtyRows() {
    this.changeset.data.forEach((row) => row.dirty.clear());
  }

  exportToExcel(
    exportOption: ExportOption = 'Current Type',
    activeFilter?: ExportActiveFilter,
  ) {
    if (this.restaurantId) {
      this.loaderService.show();
      this.service
        .exportToExcel(
          this.type,
          this.restaurantId,
          this.productTypeId,
          exportOption,
          activeFilter,
        )
        .subscribe({
          next: (data: Blob) => {
            const blob = new Blob([data], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${this.type.charAt(0).toUpperCase() + this.type.slice(1)} Export ${new Date().toLocaleString('en-US')} ${
              exportOption === 'Tier'
                ? `Tier ${this.globalEventsService.getSelectedTier()?.name ?? ''}`.trim()
                : exportOption === 'All'
                  ? exportOption
                  : `${exportOption} ${this.productTypeId}`
            }${
              activeFilter === 'Inactive'
                ? ' Inactive'
                : activeFilter === 'All'
                  ? ' Active and Inactive'
                  : ''
            }.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            this.loaderService.hide();
          },
          error: (err) => {
            this.loaderService.hide();

            this.messageService.error('Failed to export changesets');

            const showDetails = (details: unknown) => {
              if (details && (details as ValidationResult).errors) {
                this.validationErrorService.show(details as ValidationResult);
                return;
              }

              // Not a validation result (plain text, or a network failure where
              // the response never arrived): fall back to whatever description
              // is available so the popup is never left without a detail.
              const result = new ValidationResult();
              result.errors = [
                {
                  propertyName: '',
                  errorMessage:
                    typeof details === 'string' && details
                      ? details
                      : err.message || 'Unknown export error',
                  severity: 0,
                },
              ];
              this.validationErrorService.show(result);
            };

            // The export is requested with responseType 'blob', so an error
            // payload also arrives as a Blob and has to be read back as text
            // before it can be shown.
            if (err?.error instanceof Blob) {
              err.error.text().then((text: string) => {
                try {
                  showDetails(JSON.parse(text));
                } catch {
                  showDetails(text);
                }
              });
            } else {
              showDetails(err?.error);
            }
          },
        });
    }
  }

  executeFilter(
    filterService: FilterService,
    column: string,
    values: string[],
  ) {
    filterService.filter({
      filters: values.map((value) => ({
        field: column,
        operator: 'eq',
        value,
      })),
      logic: 'or',
    });
  }

  onCustomFilterChange(
    column: string,
    event: any,
    filterService: FilterService,
  ) {
    const values = event as string[];
    this.filterCache.set(column, values);

    this.executeFilter(filterService, column, values);
  }

  filterChanged(event: CompositeFilterDescriptor) {
    if (event.filters.length === 0) {
      this.filterCache.clear();
    }

    for (const field of this.filterCache.keys()) {
      if (!GridState.hasField(event, field)) {
        this.filterCache.delete(field);
      }
    }
  }
}
